/**
 * The Markdown dialect of show notes. `docs/show-notes-markup.md` in the Django
 * repo says why.
 *
 * A quotation and code are not constructs: a `>` and a backtick stay as they
 * were typed. Podcast apps do not show them, so a creator who sees a quotation in
 * the dashboard would not see it in the app. The Markdown converter of the
 * server (`PinecastExtension` in `pinecast/markdown.py`) turns off the same
 * constructs, so this list and that class have to agree.
 *
 * It is an extension of micromark. Each parser of show notes in this repo uses
 * it: `markdownToHTML` and the rich text view.
 */
export const showNotesDialect = {
  disable: {
    null: ['blockQuote', 'codeFenced', 'codeIndented', 'codeText'],
  },
};

// The code spans of show notes. Code is not a construct of the dialect, so a
// code span only marks the text whose tags stay text (see
// `escapeTagsInCodeSpans`). This is not the code span of CommonMark: it ends at
// the end of its line, so a stray backtick cannot reach the markup of another
// line. `pinecast/markdown.py` has the same rule, and `code_spans.json` holds
// the cases of both.

const LINE_ENDING = /(\r\n|\r|\n)/;
// The markers of list items at the start of a line.
const LIST_MARKERS = /^(?:(?:[-+*]|[0-9]{1,9}[.)])[ \t]+)+/;

// Raw HTML in a line (CommonMark 6.6), and an autolink (6.5). A backtick in
// them does not start a code span. These end at a place that is near: an
// attribute list, a URL and an address stop at the next `<` (a quoted value can
// pass it).
const TAG_NAME = '[A-Za-z][A-Za-z0-9-]*';
const ATTRIBUTE = String.raw`[ \t]+[A-Za-z_:][A-Za-z0-9_.:-]*(?:[ \t]*=[ \t]*(?:[^ \t"'=<>\x60]+|'[^']*'|"[^"]*"))?`;
const OPEN_TAG = String.raw`<${TAG_NAME}(?:${ATTRIBUTE})*[ \t]*\/?>`;
const CLOSING_TAG = String.raw`<\/${TAG_NAME}[ \t]*>`;
const INLINE_HTML = new RegExp(
  [
    OPEN_TAG,
    CLOSING_TAG,
    String.raw`<[A-Za-z][A-Za-z0-9+.-]{1,31}:[^\x00-\x20\x7f<>]*>`,
    String.raw`<[A-Za-z0-9.!#$%&'*+/=?^_\x60{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*>`,
  ].join('|'),
  'y',
);

// The other raw HTML ends at a marker that can be far, or can be missing: a
// comment, a processing instruction, a declaration and CDATA. The marker is
// found with `createFinds`, so that a line with many starts and no marker is read
// one time. A comment with no text, `<!-->` and `<!--->`, has no marker.
const EMPTY_COMMENT = /<!--(?:-?>)/y;
const MARKED_HTML: Array<[RegExp, string]> = [
  [/<!--/y, '-->'],
  [/<\?/y, '?>'],
  [/<![A-Za-z]/y, '>'],
  [/<!\[CDATA\[/y, ']]>'],
];

// The start of each kind of HTML block (CommonMark 4.6), and its end. The end of
// the last two is a blank line. A line in an HTML block has no code span.
const BLANK = /^$/;
const HTML_BLOCK_NAMES =
  'address|article|aside|base|basefont|blockquote|body|caption|center|col|' +
  'colgroup|dd|details|dialog|dir|div|dl|dt|fieldset|figcaption|figure|' +
  'footer|form|frame|frameset|h1|h2|h3|h4|h5|h6|head|header|hr|html|iframe|' +
  'legend|li|link|main|menu|menuitem|nav|noframes|ol|optgroup|option|p|param|' +
  'search|section|summary|table|tbody|td|tfoot|th|thead|title|tr|track|ul';
const HTML_BLOCKS: Array<[RegExp, RegExp]> = [
  [
    /^<(?:pre|script|style|textarea)(?:[ \t>]|$)/i,
    /<\/(?:pre|script|style|textarea)>/i,
  ],
  [/^<!--/, /-->/],
  [/^<\?/, /\?>/],
  [/^<![A-Za-z]/, />/],
  [/^<!\[CDATA\[/, /\]\]>/],
  [
    new RegExp(String.raw`^<\/?(?:${HTML_BLOCK_NAMES})(?:[ \t>]|\/>|$)`, 'i'),
    BLANK,
  ],
  [new RegExp(String.raw`^(?:${OPEN_TAG}|${CLOSING_TAG})[ \t]*$`), BLANK],
];

const INLINE = /\\[\s\S]|`+|</g;
const BACKTICK_RUN = /`+/g;

// The places of the end markers in one line, each found one time.
//
// A marker that does not come, as `-->` after `<!--`, is a search to the end of
// the line. A line of many `<!--` and no `-->` made one such search for each of
// them. The search for a marker at or after `start` has the answer of the last
// search of that marker when the last search began at or before `start`: its
// place if the place is not before `start`, and nothing if it found nothing. A
// new search is thus only after a place that was found and passed, and the
// searches of one marker do not read the same text twice. `start` does not go
// back for one marker. The result is the offset of the first `marker` at or
// after `start`, or -1.
function createFinds(line: string): (marker: string, start: number) => number {
  const last = new Map<string, number>();
  return (marker, start) => {
    let at = last.get(marker);
    if (at === undefined || (at >= 0 && at < start)) {
      at = line.indexOf(marker, start);
      last.set(marker, at);
    }
    return at;
  };
}

// The runs of backticks in one line, found one time, in lists by length.
//
// A search for the next run of the same length, from each opener, read to the
// end of the line when there was no such run. The search for the closer of an
// opener goes forward in the list of its length, and no run is read twice.
// `after` does not go back. The result is the offset of the first run of
// `length` backticks at or after `after`, or -1.
function createRuns(line: string): (length: number, after: number) => number {
  const starts = new Map<number, Array<number>>();
  BACKTICK_RUN.lastIndex = 0;
  for (let run = BACKTICK_RUN.exec(line); run; run = BACKTICK_RUN.exec(line)) {
    const list = starts.get(run[0].length);
    if (list) {
      list.push(run.index);
    } else {
      starts.set(run[0].length, [run.index]);
    }
  }
  const index = new Map<number, number>();
  return (length, after) => {
    const list = starts.get(length) ?? [];
    let i = index.get(length) ?? 0;
    while (i < list.length && list[i] < after) {
      i += 1;
    }
    index.set(length, i);
    return i < list.length ? list[i] : -1;
  };
}

// Where `pattern`, a sticky expression, ends when it matches at `at`, or -1.
function matchEnd(pattern: RegExp, line: string, at: number): number {
  pattern.lastIndex = at;
  return pattern.test(line) ? pattern.lastIndex : -1;
}

// The offset where the raw HTML or autolink that starts at `start` ends, or -1
// when none starts there.
function htmlEnd(
  line: string,
  start: number,
  find: (marker: string, start: number) => number,
): number {
  const empty = matchEnd(EMPTY_COMMENT, line, start);
  if (empty >= 0) {
    return empty;
  }
  for (const [opening, marker] of MARKED_HTML) {
    const found = matchEnd(opening, line, start);
    if (found >= 0) {
      const at = find(marker, found);
      if (at >= 0) {
        return at + marker.length;
      }
    }
  }
  // An address can start as a declaration or a comment, as `<!--@a.b>`.
  return matchEnd(INLINE_HTML, line, start);
}

// The code spans of one line, as offsets of their content: a run of backticks,
// and the next run of the same length. A backslash makes the backtick after it
// text, and a backtick in raw HTML or an autolink that starts before it does not
// start a code span.
function spansInLine(line: string): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  if (!line.includes('`')) {
    return spans;
  }
  const find = createFinds(line);
  const closer = createRuns(line);
  INLINE.lastIndex = 0;
  for (let match = INLINE.exec(line); match; match = INLINE.exec(line)) {
    if (match[0] === '<') {
      const end = htmlEnd(line, match.index, find);
      if (end >= 0) {
        INLINE.lastIndex = end;
      }
    } else if (match[0][0] === '`') {
      const start = INLINE.lastIndex;
      const at = closer(match[0].length, start);
      if (at >= 0) {
        spans.push([start, at]);
        INLINE.lastIndex = at + match[0].length;
      }
    }
  }
  return spans;
}

/**
 * The content of each code span in `markdown`, as offsets.
 *
 * A code span does not cross the end of a line. A line in an HTML block has no
 * code span: the block starts at a line that starts a block in CommonMark (also
 * after the markers of list items, and also after text), and it ends at its end
 * marker or at a blank line. Thus imported HTML keeps its markup.
 */
function codeSpans(markdown: string): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  let htmlEnd: RegExp | null = null; // the end of the open HTML block
  let start = 0;
  for (const line of markdown.split(LINE_ENDING)) {
    if (!/^(?:\r\n|\r|\n)$/.test(line)) {
      const text = line.replace(/^[ \t]+/, '');
      // The text after the markers of list items.
      const content = text.replace(LIST_MARKERS, '');
      if (htmlEnd) {
        if (htmlEnd.test(text)) {
          htmlEnd = null;
        }
      } else {
        const html = HTML_BLOCKS.find(([blockStart]) =>
          blockStart.test(content),
        );
        if (html) {
          htmlEnd = html[1].test(text) ? null : html[1];
        } else {
          for (const [from, to] of spansInLine(line)) {
            spans.push([start + from, start + to]);
          }
        }
      }
    }
    start += line.length;
  }
  return spans;
}

/**
 * Writes each `<` and `>` in a code span as an entity.
 *
 * Code is not a construct of the dialect, so the backticks stay as typed. The
 * text between them must stay text too: a tag that the sanitizer allows, as
 * `<h2>` in "Use the `<h2>` tag.", would otherwise be markup, and the rest of
 * the notes would be a heading. A code span ends at the end of its line, and an
 * HTML block has none, so a stray backtick, as in "It`s", does not change the
 * markup of another line or of imported HTML. `escape_tags_in_code_spans` in
 * `pinecast/markdown.py` has the same rule, so the two sides give the same text.
 */
export function escapeTagsInCodeSpans(markdown: string): string {
  if (!markdown.includes('`')) {
    return markdown;
  }
  let out = '';
  let done = 0; // `markdown` up to here is in `out`
  for (const [start, end] of codeSpans(markdown)) {
    out += markdown.slice(done, start);
    out += markdown
      .slice(start, end)
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    done = end;
  }
  return out + markdown.slice(done);
}
